import React, { useEffect, useState } from "react";
import { Bell, Menu } from "lucide-react";
import Alerts from "../common/Alerts";
import { useExpirationAlerts } from "../common/useExpirationAlerts.jsx";
import {
  ALERT_MIN_ACTIVE_HOURS,
  ALERT_EXPIRES_MAX_HOURS,
} from "../constants.js";

import { useScheduledSettingsTrigger } from "../../services/API/ControllerAPI/GetUpdatedSettings.jsx";

const TopBar = ({ onMenuClick }) => {
  // Controls alerts popup
  const [showAlerts, setShowAlerts] = useState(false);

  // Stores the number of alerts that have not been viewed yet.
  const [unseenAlertCount, setUnseenAlertCount] = useState(0);

  // Stores the device IDs that failed during the latest alerts refresh.
  const [failedControllerIds, setFailedControllerIds] = useState([]);

  // Tracks the initial alerts refresh started from the TopBar.
  const [alertsRefreshing, setAlertsRefreshing] = useState(false);

  // Loads alerts and exposes a manual Device Settings refresh.
  const { alerts, refetchDeviceSettings } = useExpirationAlerts();

  // Refreshes controller settings and returns each device refresh status.
  const refreshControllerSettingsMutation = useScheduledSettingsTrigger();

  // Refreshes Device Settings once every 24 hours.
  useEffect(() => {
    const DAILY_REFRESH_MS = 24 * 60 * 60 * 1000;

    const intervalId = setInterval(() => {
      refetchDeviceSettings();
    }, DAILY_REFRESH_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetchDeviceSettings]);

  // Creates a stable unique key for one alert.
  const getAlertKey = (alert) => {
    const createdTime =
      alert?.createdAt instanceof Date
        ? alert.createdAt.getTime()
        : new Date(alert?.createdAt).getTime();

    return `${alert?.deviceId}-${alert?.settingName}-${createdTime}`;
  };

  // Loads the alerts that have already been viewed.
  const getViewedAlertKeys = () => {
    try {
      const stored = localStorage.getItem("viewedAlertKeys");

      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  // // Creates a stable signature for the current alert state.
  // const alertsSignature = alerts
  //   .map((alert) => {
  //     const expiryTime =
  //       alert?.expiryDate instanceof Date
  //         ? alert.expiryDate.getTime()
  //         : new Date(alert?.expiryDate).getTime();

  //     return `${alert?.deviceId}-${alert?.settingName}-${expiryTime}`;
  //   })
  //   .sort()
  //   .join("|");

  // // Counts the same alerts displayed by the "All" filter in the alerts popup.
  // const alertCount = alerts.filter((alert) => {
  //   const remainingHours = Number(alert?.remainingHours);

  //   if (!Number.isFinite(remainingHours)) {
  //     return false;
  //   }

  // Keeps only alerts that are valid for the notification badge.
  const validAlerts = alerts.filter((alert) => {
    const remainingHours = Number(alert?.remainingHours);

    if (!Number.isFinite(remainingHours)) {
      return false;
    }

    return (
      remainingHours <= 0 ||
      (remainingHours > 0 &&
        remainingHours >= ALERT_MIN_ACTIVE_HOURS &&
        remainingHours <= ALERT_EXPIRES_MAX_HOURS)
    );
  });

  // Gets only the unique device IDs that currently have valid alerts.
  const alertRefreshDeviceIds = [
    ...new Set(validAlerts.map((alert) => alert?.deviceId).filter(Boolean)),
  ];

  // Loads the alerts that were already viewed.
  const viewedAlertKeys = getViewedAlertKeys();

  // Keeps only alerts that have never been viewed.
  const unseenAlerts = validAlerts.filter(
    (alert) => !viewedAlertKeys.includes(getAlertKey(alert)),
  );

  // // Counts only new alerts.
  const alertCount = unseenAlerts.length;

  //     return (
  //       remainingHours <= 0 ||
  //       (remainingHours > 0 &&
  //         remainingHours >= ALERT_MIN_ACTIVE_HOURS &&
  //         remainingHours <= ALERT_EXPIRES_MAX_HOURS)
  //     );
  //   }).length;

  // Stores the latest alert state after the popup is opened
  // and the refreshed alerts have been rendered.
  // useEffect(() => {
  //   if (!showAlerts || !alertsSignature) {
  //     return;
  //   }

  //   // Stores the latest alert signature as viewed.
  //   localStorage.setItem("lastViewedAlertsSignature", alertsSignature);

  //   // Updates the viewed alert signature in React.
  //   setLastViewedAlertsSignature(alertsSignature);
  // }, [showAlerts, alertsSignature]);

  // // Updates the badge when the day changes or the current alerts change.
  // useEffect(() => {
  //   if (alertCount <= 0) {
  //     setUnseenAlertCount(0);
  //     return;
  //   }

  //   // Creates a local date key for the current day.
  //   const now = new Date();
  //   const todayKey = `${now.getFullYear()}-${String(
  //     now.getMonth() + 1,
  //   ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  //   // Gets the last viewed alert state.
  //   const lastViewedAlertDate = localStorage.getItem("lastViewedAlertDate");

  //   // Shows the alerts when they have not been viewed today.
  //   if (lastViewedAlertDate !== todayKey) {
  //     setUnseenAlertCount(alertCount);
  //     return;
  //   }

  //   // Shows the badge again when the alert state changed after it was viewed.
  //   if (
  //     lastViewedAlertsSignature &&
  //     alertsSignature !== lastViewedAlertsSignature
  //   ) {
  //     setUnseenAlertCount(alertCount);
  //     return;
  //   }

  //   // Keeps the badge hidden when nothing changed.
  //   setUnseenAlertCount(0);
  // }, [alertCount, alertsSignature, lastViewedAlertsSignature]);

  // Updates the badge with only alerts that have never been viewed.
  useEffect(() => {
    setUnseenAlertCount(alertCount);
  }, [alertCount]);

  return (
    <div className="bg-stone-100 rounded-t-lg pt-3 h-20 px-5 py-2 flex flex-col justify-between relative">
      <div className="flex mt-3 px-1 justify-between items-center">
        <div className="flex items-center gap-2">
          {/* Icon Menu */}{" "}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-full text-[#222731] hover:bg-stone-200"
            aria-label="Open navigation menu"
          >
            {/* Icon Menu */}
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Alert button */}
        <div className="flex items-center gap-3 p-2  rounded-full  hover:bg-stone-200">
          <button
            onClick={async () => {
              // Opens the alerts popup immediately.
              setShowAlerts(true);

              // Shows the loading state while the alert data is being refreshed.
              setAlertsRefreshing(true);

              try {
                // Refreshes controller settings before updating the alerts.
                const refreshResult =
                  await refreshControllerSettingsMutation.mutateAsync({
                    controllerIds: alertRefreshDeviceIds,
                  });

                // Stores the device IDs that failed during the refresh.
                const failedIds =
                  refreshResult.details
                    ?.filter((item) => item?.status === "error")
                    .map((item) => item?.deviceId)
                    .filter(Boolean) ?? [];

                setFailedControllerIds(failedIds);

                // Refreshes Device Settings after the controller refresh finishes.
                await refetchDeviceSettings();

                // Loads alerts that were already viewed.
                const viewedKeys = getViewedAlertKeys();

                // Marks the currently visible alerts as viewed.
                const currentAlertKeys = validAlerts.map((alert) =>
                  getAlertKey(alert),
                );

                // Keeps all previously viewed alerts and adds only new ones.
                const updatedViewedKeys = Array.from(
                  new Set([...viewedKeys, ...currentAlertKeys]),
                );

                // Persists viewed alerts between sessions and days.
                localStorage.setItem(
                  "viewedAlertKeys",
                  JSON.stringify(updatedViewedKeys),
                );

                // Clears the notification badge.
                setUnseenAlertCount(0);
              } finally {
                // Hides the loading state after the alert refresh finishes.
                setAlertsRefreshing(false);
              }
            }}
            className="relative"
          >
            <Bell className="w-6 h-6 text-[#222731]" />

            {unseenAlertCount > 0 && (
              <span className="absolute top-1 right-1 flex h-4 w-4">
                <span
                  className="relative inline-flex items-center justify-center rounded-full h-4 w-4 bg-red-500 text-white text-xs"
                  style={{ fontSize: "0.6rem" }}
                >
                  {unseenAlertCount}
                </span>
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Alerts popup */}
      {showAlerts && (
        <Alerts
          onClose={() => setShowAlerts(false)}
          initialFailedControllerIds={failedControllerIds}
          initialRefreshing={alertsRefreshing}
        />
      )}
    </div>
  );
};

export default TopBar;
